// Which data path feeds the dashboard, and whether to cross-check them.
//
// Three modes rather than a boolean, because the interesting state is the middle
// one: run BOTH paths and report any field that disagrees. The verification
// harnesses prove the arithmetic on combinations I chose; compare mode proves it
// on whatever is actually clicked — a scope never sampled, an odd As-of date, a
// filter mix nobody thought of.
//
// It also catches a class the harnesses cannot. They call the builders directly,
// so they cannot see a hook passing the wrong granularity or dropping a filter:
// the maths would be right and the answer still wrong.
//
//   ?data=rollup    rollup rows only -- the default since 2026-08-28
//   ?data=raw       raw rows only, the old behaviour, kept as the way back
//   ?data=compare   both, with mismatches logged to the console
//
// The choice is sticky in localStorage so it survives a reload, and the URL wins
// when present.

export type DataPath = "raw" | "rollup" | "compare";

const KEY = "fetchtime.dataPath";
const VALID: DataPath[] = ["raw", "rollup", "compare"];

function fromUrl(): DataPath | null {
  try {
    const v = new URLSearchParams(window.location.search).get("data");
    return v && (VALID as string[]).includes(v) ? (v as DataPath) : null;
  } catch {
    return null;
  }
}

export function getDataPath(): DataPath {
  const url = fromUrl();
  if (url) {
    try {
      localStorage.setItem(KEY, url);
    } catch {
      // Storage unavailable — the URL still governs this page load.
    }
    return url;
  }
  try {
    const stored = localStorage.getItem(KEY);
    if (stored && (VALID as string[]).includes(stored)) return stored as DataPath;
  } catch {
    // Fall through to the default.
  }
  // Rollup is the default. At YTD on dev this is the difference between fetching
  // 271.5 MB of JSON and 81.7 MB, and between 132.2 MB of browser heap and 80.4
  // MB -- the entire point of the exercise, and worth nothing while it was opt-in.
  //
  // ?data=raw still works and is the way back: the raw path is untouched, which is
  // why the ~1,500 lines that serve it are deliberately still here. Delete them
  // only once this default has been quiet in production, because they are what a
  // rollback would run.
  return "rollup";
}

export function setDataPath(path: DataPath): void {
  try {
    localStorage.setItem(KEY, path);
  } catch {
    // Non-fatal: the caller reloads with ?data= instead.
  }
}

/** True when the rollup path should be fetched (on its own or for comparison). */
export function needsRollup(path: DataPath): boolean {
  return path === "rollup" || path === "compare";
}

/** True when the raw path should be fetched. */
export function needsRaw(path: DataPath): boolean {
  return path === "raw" || path === "compare";
}
